import React, { useEffect, useMemo, useState } from 'react';
import Swal from '../librarianSwal';
import { useActiveLibraryId } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import '../lib-page-header.css';
import '../lib-typography.css';
import '../BookList.css';
import './NotificationList.css';
import FilterSelect from '../components/FilterSelect';
import Pagination from '../components/Pagination';

import {
  getNotificationsByLibrary,
  markNotificationAsRead,
  getErrorMessage
} from '../../api/api';
import { readPrefetched } from '../../api/prefetchCache';

const NOTIFICATIONS_RESOURCE = 'notifications';
const PAGE_SIZE = 10;

const NotificationList = () => {
  const libraryId = useActiveLibraryId();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchNotifications = async () => {
    try {
      if (!libraryId) return;

      setLoading(true);

      const data = await getNotificationsByLibrary(libraryId);
      setNotifications(data || []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const warm = readPrefetched(NOTIFICATIONS_RESOURCE, libraryId);
    if (warm) setNotifications(warm || []);

    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const filteredNotifications = useMemo(() => {
    return notifications.filter((item) => {
      if (typeFilter && String(item.notification_type || '').toLowerCase() !== typeFilter) return false;
      if (statusFilter) {
        const isRead = Boolean(item.is_read);
        if (statusFilter === 'read' && !isRead) return false;
        if (statusFilter === 'unread' && isRead) return false;
      }
      const keyword = search.toLowerCase();
      if (!keyword) return true;
      return (
        String(item.title || '').toLowerCase().includes(keyword) ||
        String(item.message || '').toLowerCase().includes(keyword) ||
        String(item.notification_type || '').toLowerCase().includes(keyword) ||
        String(item.member_name || item.parent_name || '').toLowerCase().includes(keyword)
      );
    });
  }, [notifications, typeFilter, statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / PAGE_SIZE));
  const paginatedNotifications = filteredNotifications.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, statusFilter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const getReadBadge = (isRead) => (isRead ? 'bg-primary' : 'bg-warning text-dark');

  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((item) => (item.id === id ? { ...item, is_read: 1 } : item))
      );
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  return (
    <div className="container-fluid mt-3 nf-page">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-bell" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Notifications</h3>
            <div className="lib-page-header__subtitle" title="Personal system alerts generated from library activities.">
              Personal system alerts generated from library activities such as issues, due dates, fines, reservations and other member-specific events. For general notices to a wider audience, see Announcements.
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-3 table-full-card">
        <div className="card-body">
          <div className="book-filter-row mb-3">
            <input
              type="text"
              className="book-search-input"
              placeholder="Member, title, message, type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FilterSelect
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { value: '', label: 'All Types' },
                { value: 'book_issued', label: 'Book Issued' },
                { value: 'book_returned', label: 'Book Returned' },
                { value: 'book_overdue', label: 'Book Overdue' },
                { value: 'fine_created', label: 'Fine Created' },
                { value: 'reservation_created', label: 'Reservation Created' },
                { value: 'reservation_ready', label: 'Reservation Ready' },
                { value: 'reservation_cancelled', label: 'Reservation Cancelled' },
              ]}
            />
            <FilterSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: '', label: 'All Status' },
                { value: 'read', label: 'Read' },
                { value: 'unread', label: 'Unread' },
              ]}
            />
            <button
              type="button"
              className="book-reset-btn"
              onClick={() => { setSearch(''); setTypeFilter(''); setStatusFilter(''); }}
            >
              Reset
            </button>
          </div>

          {showLoader ? (
            <Loader />
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle notif-table lib-table-cell">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Member</th>
                    <th className="notif-col-type">Type</th>
                    <th>Title</th>
                    <th>Message</th>
                    <th>Status</th>
                    <th className="notif-col-created">Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedNotifications.length > 0 ? (
                    paginatedNotifications.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                        <td>
                          <strong>{item.member_name || item.parent_name || '-'}</strong>
                          <div className="small text-muted">{item.mobile || '-'}</div>
                        </td>
                        <td className="notif-col-type">{item.notification_type || '-'}</td>
                        <td>{item.title || '-'}</td>
                        <td>{item.message || '-'}</td>
                        <td>
                          <span className={`badge ${getReadBadge(item.is_read)}`}>
                            {item.is_read ? 'Read' : 'Unread'}
                          </span>
                        </td>
                        <td className="notif-col-created">
                          {item.created_at ? String(item.created_at).slice(0, 10) : '-'}
                        </td>
                        <td>
                          {!item.is_read ? (
                            <div className="book-action-buttons">
                              <button
                                type="button"
                                className="book-action-btn success"
                                onClick={() => handleMarkAsRead(item.id)}
                              >
                                <i className="ti ti-circle-check" /> Mark as Read
                              </button>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No notifications found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredNotifications.length}
                itemLabel="notifications"
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationList;